import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

interface Late { id: string; post_slug: string; variant: number; was: string; proposed: string }
interface Ready { boards: Record<string, number>; broken: { post_slug: string; link: string }[]; untracked: number; late: Late[]; active: boolean; connected: boolean; board_ids: Record<string, string> }

export function PinReadiness({ onChanged }: { onChanged: () => void }) {
  const [r, setR] = useState<Ready | null>(null);
  const [ids, setIds] = useState<Record<string, string>>({});
  const [from, setFrom] = useState("");
  const load = () => api.get("/admin/pins/readiness", { params: from ? { start: from } : {} }).then((x) => { setR(x.data); setIds(x.data.board_ids); }).catch(() => {});
  useEffect(() => { load(); }, [from]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  const start = from || r.late[0]?.proposed;
  const apply = async () => {
    if (!start || !window.confirm(`Move the ${r.late.length} past-date pins to the proposed dates? No other pins change.`)) return;
    try { const { data } = await api.post("/admin/pins/readiness/apply-dates", { start }); toast.success(`${data.moved} pins moved`); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveIds = (active: boolean) => api.put("/admin/pins/launch", { active, boards: ids }).then(() => { toast.success(active ? "Auto-posting is on" : "Saved"); load(); }).catch((e) => toast.error(formatApiError(e)));
  return (
    <section className="paper-card mb-6 space-y-4 p-5" data-testid="pin-readiness">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-extrabold text-[#23324A]">Launch readiness</h2>
        <span className="rounded-full bg-[#FFF8E5] px-3 py-1 text-xs font-bold text-[#23324A]" data-testid="pin-autopost-status">
          {r.active ? "Auto-posting ON" : r.connected ? "Pinterest connected, auto-posting OFF" : "Pinterest not connected, auto-posting OFF"}
        </span>
      </div>
      <ul className="grid gap-2 text-sm text-[#23324A] sm:grid-cols-3">
        <li data-testid="pin-check-links">{r.broken.length ? `${r.broken.length} pins have a link problem` : "All pin links point to live articles"}</li>
        <li data-testid="pin-check-tracking">{r.untracked ? `${r.untracked} pins without tracking tags` : "All pins have Pinterest tracking tags"}</li>
        <li data-testid="pin-check-late">{r.late.length ? `${r.late.length} pins have past dates` : "No pins with past dates"}</li>
      </ul>
      {r.late.length > 0 && (
        <div data-testid="pin-late-list">
          <p className="mb-2 flex flex-wrap items-center gap-2 text-sm font-bold text-[#23324A]">Proposed new dates (third daily slot, other pins unchanged), starting
            <input type="date" value={from || r.late[0]?.proposed || ""} onChange={(e) => setFrom(e.target.value)} className="rounded-lg border-2 border-[#23324A]/20 px-2 py-0.5 text-xs" data-testid="pin-late-start" />
          </p>
          <ul className="max-h-40 overflow-y-auto text-xs text-[#23324A]/80">{r.late.map((l) => <li key={l.id}>{l.post_slug} (design {l.variant + 1}): {l.was} → <b>{l.proposed}</b></li>)}</ul>
          <button className="btn-outline mt-2" onClick={apply} data-testid="pin-apply-dates">Approve these dates</button>
        </div>
      )}
      <details data-testid="pin-boards">
        <summary className="cursor-pointer text-sm font-bold text-[#23324A]">Boards ({Object.keys(r.boards).length}) and Pinterest board IDs</summary>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(r.boards).map(([b, n]) => (
            <label key={b} className="flex items-center gap-2 text-sm text-[#23324A]">
              <span className="w-56 shrink-0">{b} <span className="text-[#23324A]/50">({n})</span></span>
              <input value={ids[b] || ""} onChange={(e) => setIds({ ...ids, [b]: e.target.value })} placeholder="board ID" className="min-w-0 flex-1 rounded-lg border-2 border-[#23324A]/20 px-2 py-1 text-xs" data-testid={`board-id-${b}`} />
            </label>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button className="btn-outline" onClick={() => saveIds(r.active)} data-testid="board-ids-save">Save board IDs</button>
          {r.connected && <button className={r.active ? "btn-ghost" : "btn-coral"} onClick={() => saveIds(!r.active)} data-testid="autopost-toggle">{r.active ? "Turn auto-posting off" : "Turn auto-posting on"}</button>}
        </div>
      </details>
    </section>
  );
}
